/**
 * Event temporal helpers.
 *
 * Event.timezone is authoritative.
 * datetime-local values represent wall-clock values and must never be
 * interpreted implicitly through the browser timezone.
 */

const pad = (value) => String(value).padStart(2, "0");

const partsForTimezone = (instant, timeZone) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

  return Object.fromEntries(
    formatter
      .formatToParts(instant)
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value])
  );
};

export const instantToEventLocalInput = (
  timestamp,
  timeZone = "Europe/Paris"
) => {
  if (!timestamp) return "";

  const instant = new Date(timestamp);

  if (Number.isNaN(instant.getTime())) {
    throw new Error("Invalid Event timestamp.");
  }

  const parts = partsForTimezone(instant, timeZone);

  return (
    `${parts.year}-${parts.month}-${parts.day}` +
    `T${parts.hour}:${parts.minute}`
  );
};

/**
 * Keep datetime-local as a local wall-clock value.
 *
 * The backend combines this value with Event.timezone and performs
 * authoritative DST validation / UTC normalization.
 */
export const eventLocalInputToApi = (value) => {
  if (!value) return null;

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  if (!match) {
    throw new Error("Invalid Event local datetime.");
  }

  const [, year, month, day, hour, minute, second] = match;

  return (
    `${year}-${month}-${day}` +
    `T${hour}:${minute}:${second || "00"}`
  );
};


export const instantToEventDateInput = (
  timestamp,
  timeZone = "Europe/Paris"
) => {
  if (!timestamp) return "";

  const instant = new Date(timestamp);

  if (Number.isNaN(instant.getTime())) {
    throw new Error("Invalid Event timestamp.");
  }

  const parts = partsForTimezone(instant, timeZone);

  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const eventDateInputToApi = (value) => {
  if (!value) return null;

  const candidate = String(value);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    throw new Error("Invalid Event calendar date.");
  }

  return candidate;
};

export const nextEventDateInput = (value) => {
  const candidate = eventDateInputToApi(value);

  if (!candidate) return "";

  const [year, month, day] = candidate.split("-").map(Number);
  const instant = new Date(Date.UTC(year, month - 1, day + 1));

  return (
    `${instant.getUTCFullYear()}-` +
    `${pad(instant.getUTCMonth() + 1)}-` +
    `${pad(instant.getUTCDate())}`
  );
};

export const eventInstantToCalendarValue = (
  timestamp,
  timeZone = "Europe/Paris",
  isAllDay = false
) => {
  if (isAllDay) {
    return instantToEventDateInput(timestamp, timeZone);
  }

  const local = instantToEventLocalInput(timestamp, timeZone);

  return local ? `${local}:00` : "";
};

export const calendarDateToLocalApi = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    throw new Error("Invalid calendar datetime.");
  }

  return (
    `${value.getFullYear()}-${pad(value.getMonth() + 1)}-` +
    `${pad(value.getDate())}T${pad(value.getHours())}:` +
    `${pad(value.getMinutes())}:${pad(value.getSeconds())}`
  );
};

export const calendarDateToDateInput = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    throw new Error("Invalid calendar date.");
  }

  return (
    `${value.getFullYear()}-${pad(value.getMonth() + 1)}-` +
    `${pad(value.getDate())}`
  );
};

export const formatEventDate = (
  timestamp,
  timeZone = "Europe/Paris",
  locale = "fr-FR"
) =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(timestamp));

export const formatEventTime = (
  timestamp,
  timeZone = "Europe/Paris",
  locale = "fr-FR"
) =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
